"use client"

import { useState } from "react"

import { useForm } from "@tanstack/react-form"
import { useQuery } from "@tanstack/react-query"

import { Info, X } from "lucide-react"

import { Button } from "@/components/ui/Button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog"
import { Field, FieldError } from "@/components/ui/Field"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select"
import { Spinner } from "@/components/ui/Spinner"

import scribsBackground from "../../assets/images/scribs.png"
import { isValidEmail } from "../utils/email"
import { ColleagueInput, ColleagueSuggestion } from "./ColleagueInput"
import { SubscriptionFinancialAidController } from "./SubscriptionFinancialAid.controller"

export interface FinancialFormData {
  name: string
  email: string
  role: string
  confirmation: boolean
  signature: string
  invitedColleagues: string[]
}

interface SubscriptionFinancialAidProps {
  controller: SubscriptionFinancialAidController
}

const ROLE_OPTIONS = [
  { value: "Administrator", label: "Administrator" },
  { value: "Dean", label: "Dean" },
  { value: "Principal", label: "Principal" },
  { value: "Vice Principal", label: "Vice Principal" },
  { value: "Assistant Principal", label: "Assistant Principal" },
  { value: "Secretary", label: "Secretary" },
  { value: "Superintendent", label: "Superintendent" },
]

export function SubscriptionFinancialAid({
  controller,
}: SubscriptionFinancialAidProps) {
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const { data: orgData } = useQuery({
    queryKey: ["subscriptionFinancialAid", "organizationData"],
    queryFn: controller.loadOrganizationData,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: 2,
  })

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      role: "",
      confirmation: false as boolean,
      signature: "",
      invitedColleagues: [] as string[],
    } satisfies FinancialFormData,
    onSubmit: async ({ value }) => {
      await controller.submit(value)
      setShowSuccessDialog(true)
    },
  })

  function searchColleagues(query: string): ColleagueSuggestion[] {
    if (!query || !orgData?.mdrPeers?.length) {
      return []
    }

    const lowercaseQuery = query.toLowerCase()
    return orgData.mdrPeers.filter(
      (colleague) =>
        colleague.email.toLowerCase().includes(lowercaseQuery) ||
        colleague.name.toLowerCase().includes(lowercaseQuery),
    )
  }

  function handleDialogOpenChange(open: boolean) {
    setShowSuccessDialog(open)
    if (!open) {
      form.reset()
    }
  }

  function handleGoToSubscription() {
    controller.goToSubscriptionPage()
    setShowSuccessDialog(false)
    form.reset()
  }

  return (
    <>
      <Dialog open={showSuccessDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="tw-max-w-2xl tw-font-sansAlt">
          <DialogHeader>
            <DialogTitle className="tw-text-center tw-text-2xl tw-font-black tw-text-primary tw-mb-2">
              🎉 Your application has been approved!
            </DialogTitle>
            <div className="tw-text-center tw-space-y-4 tw-py-4">
              <div className="tw-inline-block tw-bg-gray-100 tw-text-gray-600 tw-font-bold tw-px-4 tw-py-2 tw-rounded-sm tw-text-sm tw-mb-2">
                ⚡ You secured 1 of only 12 spots available this month
              </div>

              <div className="tw-bg-gradient-to-r tw-border-2 tw-border-primary tw-rounded-xl tw-p-6 tw-shadow-sm">
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-3 tw-mb-2">
                  <span className="tw-text-3xl tw-font-bold tw-text-red-600 tw-line-through tw-decoration-2">
                    $19.99/mo
                  </span>
                  <span className="tw-text-4xl tw-font-black tw-text-primary">
                    $4.99/mo
                  </span>
                </div>

                <p className="tw-text-xs tw-text-gray-600 tw-font-semibold">
                  Expert Plan • Next 3 Months
                </p>
              </div>
            </div>
          </DialogHeader>
          <DialogFooter className="tw-flex-col tw-gap-3 sm:tw-flex-col tw-mt-2">
            <Button
              onClick={handleGoToSubscription}
              className="tw-w-full tw-bg-primary hover:tw-from-green-100 hover:tw-to-green-600 tw-text-white tw-font-bold tw-text-lg tw-py-6 tw-shadow-lg"
              size="lg"
            >
              Claim My 75% Discount Now →
            </Button>
            <DialogClose asChild>
              <button className="tw-w-full tw-text-sm tw-text-gray-500 hover:tw-text-gray-700 tw-underline tw-py-2">
                I'll do this later
              </button>
            </DialogClose>
            <div className="tw-w-full tw-text-center tw-text-xs tw-no-underline">
              Use coupon code{" "}
              <span className="tw-font-black">
                {controller.getFinancialAidCoupon().toUpperCase()}
              </span>{" "}
              when ready to purchase your subscription
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div
        className="tw-overflow-auto tw-font-sansAlt"
        style={{ height: `${window.innerHeight / 1.6}px` }}
      >
        <div
          className="tw-min-h-screen tw-py-8 tw-px-4"
          style={{
            backgroundImage: `url(${scribsBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="tw-max-w-5xl tw-mx-auto">
            <div className="tw-text-center tw-mb-8">
              <div className="tw-text-4xl tw-font-black tw-text-white tw-mb-2">
                Individual Subscription Financial Aid
              </div>
              <div className="tw-text-xl tw-text-white tw-font-semibold">
                Complete both steps to submit your application
              </div>
            </div>

            <div className="tw-bg-amber-50 tw-border-l-4 tw-border-amber-400 tw-p-4 tw-mb-8 tw-rounded-r-lg">
              <div className="tw-flex tw-items-start tw-gap-3">
                <Info className="tw-w-5 tw-h-5 tw-text-amber-600 tw-flex-shrink-0 tw-mt-0.5" />
                <p className="tw-text-sm tw-text-amber-800 tw-leading-relaxed">
                  If your school cannot afford the purchase of an individual
                  subscription plan, we have need-based financial aid. Please
                  only apply if you've pursued all other funding options.
                </p>
              </div>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
            >
              <div className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-overflow-hidden">
                <div className="tw-p-8">
                  <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8">
                    <div className="tw-space-y-6">
                      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
                        <div className="tw-w-10 tw-h-10 tw-bg-primary tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold">
                          1
                        </div>
                        <h2 className="tw-text-xl tw-font-bold tw-text-gray-600">
                          Administrator Details
                        </h2>
                      </div>
                      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                        This information helps us verify your school's
                        eligibility and process your application.
                      </p>

                      <div className="tw-space-y-4">
                        <form.Field
                          name="name"
                          validators={{
                            onChange: ({ value }) =>
                              !value.trim() ? "Name is required" : undefined,
                          }}
                        >
                          {(field) => (
                            <Field>
                              <label
                                htmlFor={field.name}
                                className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2"
                              >
                                Full Name{" "}
                                <span className="tw-text-red-500">*</span>
                              </label>
                              <input
                                id={field.name}
                                type="text"
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                placeholder="Enter administrator's name"
                                className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-sm tw-text-sm tw-text-gray-900 tw-transition-all focus:tw-outline-none focus:tw-ring-2 ${
                                  field.state.meta.errors.length
                                    ? "tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                                    : "tw-border-gray-300 focus:tw-border-green-600 focus:tw-ring-green-600"
                                }`}
                              />
                              <FieldError>
                                {field.state.meta.errors.length > 0 && (
                                  <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                                    <X className="tw-w-4 tw-h-4" />
                                    {field.state.meta.errors[0]}
                                  </span>
                                )}
                              </FieldError>
                            </Field>
                          )}
                        </form.Field>

                        <form.Field
                          name="email"
                          validators={{
                            onBlur: ({ value }) => {
                              if (!value.trim()) return "Email is required"
                              if (!isValidEmail(value.trim()))
                                return "Please enter a valid email"
                              return undefined
                            },
                          }}
                        >
                          {(field) => (
                            <Field>
                              <label
                                htmlFor={field.name}
                                className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2"
                              >
                                Email Address{" "}
                                <span className="tw-text-red-500">*</span>
                              </label>
                              <input
                                id={field.name}
                                type="email"
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                placeholder="admin@school.edu"
                                className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-sm tw-text-sm tw-text-gray-900 tw-transition-all focus:tw-outline-none focus:tw-ring-2 ${
                                  field.state.meta.errors.length
                                    ? "tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                                    : "tw-border-gray-300 focus:tw-border-green-600 focus:tw-ring-green-600"
                                }`}
                              />
                              <FieldError>
                                {field.state.meta.errors.length > 0 && (
                                  <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                                    <X className="tw-w-4 tw-h-4" />
                                    {field.state.meta.errors[0]}
                                  </span>
                                )}
                              </FieldError>
                            </Field>
                          )}
                        </form.Field>

                        <form.Field
                          name="role"
                          validators={{
                            onChange: ({ value }) =>
                              !value.trim() ? "Role is required" : undefined,
                          }}
                        >
                          {(field) => (
                            <Field>
                              <label
                                htmlFor={field.name}
                                className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2"
                              >
                                Role/Position{" "}
                                <span className="tw-text-red-500">*</span>
                              </label>
                              <Select
                                value={field.state.value}
                                onValueChange={field.handleChange}
                              >
                                <SelectTrigger
                                  className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-sm tw-text-gray-900 tw-transition-all focus:tw-outline-none focus:tw-ring-2 ${
                                    field.state.meta.errors.length
                                      ? "tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                                      : "tw-border-gray-300 focus:tw-border-green-600 focus:tw-ring-green-600"
                                  }`}
                                >
                                  <SelectValue placeholder="Select role..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {ROLE_OPTIONS.map((option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                      className="tw-text-2xl tw-font-sansAlt"
                                    >
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FieldError>
                                {field.state.meta.errors.length > 0 && (
                                  <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                                    <X className="tw-w-4 tw-h-4" />
                                    {field.state.meta.errors[0]}
                                  </span>
                                )}
                              </FieldError>
                            </Field>
                          )}
                        </form.Field>

                        <form.Field
                          name="signature"
                          validators={{
                            onChange: ({ value }) =>
                              !value.trim()
                                ? "Signature is required"
                                : undefined,
                          }}
                        >
                          {(field) => (
                            <Field>
                              <label
                                htmlFor={field.name}
                                className="tw-block tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-2"
                              >
                                Digital Signature{" "}
                                <span className="tw-text-red-500">*</span>
                              </label>
                              <input
                                id={field.name}
                                type="text"
                                value={field.state.value}
                                onBlur={field.handleBlur}
                                onChange={(e) =>
                                  field.handleChange(e.target.value)
                                }
                                placeholder="Type your full name"
                                style={{ fontFamily: "cursive" }}
                                className={`tw-w-full tw-px-4 tw-py-3 tw-border tw-rounded-sm tw-text-sm tw-text-gray-900 tw-transition-all focus:tw-outline-none focus:tw-ring-2 tw-text-xl ${
                                  field.state.meta.errors.length
                                    ? "tw-border-red-300 tw-bg-red-50 focus:tw-ring-red-500"
                                    : "tw-border-gray-300 focus:tw-border-green-600 focus:tw-ring-green-600"
                                }`}
                              />
                              <FieldError>
                                {field.state.meta.errors.length > 0 && (
                                  <span className="tw-flex tw-text-sm tw-items-center tw-gap-1 tw-pt-2">
                                    <X className="tw-w-4 tw-h-4" />
                                    {field.state.meta.errors[0]}
                                  </span>
                                )}
                              </FieldError>
                            </Field>
                          )}
                        </form.Field>

                        <form.Field
                          name="confirmation"
                          validators={{
                            onChange: ({ value }) =>
                              !value
                                ? "You must confirm this statement"
                                : undefined,
                          }}
                        >
                          {(field) => (
                            <div
                              className={`tw-p-4 tw-border-2 tw-rounded-sm tw-transition-all ${
                                field.state.meta.errors.length
                                  ? "tw-border-red-300 tw-bg-red-50"
                                  : "tw-border-gray-200 tw-bg-gray-50"
                              }`}
                            >
                              <label className="tw-flex tw-items-start tw-cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={field.state.value}
                                  onBlur={field.handleBlur}
                                  onChange={(e) =>
                                    field.handleChange(e.target.checked)
                                  }
                                  className="tw-mt-1 tw-w-5 tw-h-5 tw-border-gray-300tw-rounded focus:tw-ring-2 focus:tw-ring-primary"
                                  style={{ accentColor: "#25C582" }}
                                />
                                <span className="tw-ml-3 tw-text-sm tw-text-gray-700 tw-leading-relaxed">
                                  I certify that our school does not have the
                                  financial means to purchase an Individual
                                  Subscription plan.
                                </span>
                              </label>
                              {field.state.meta.errors.length > 0 && (
                                <p className="tw-text-red-600 tw-text-sm tw-mt-2 tw-ml-8 tw-flex tw-items-center tw-gap-1">
                                  <X className="tw-w-4 tw-h-4" />
                                  {field.state.meta.errors[0]}
                                </p>
                              )}
                            </div>
                          )}
                        </form.Field>
                      </div>
                    </div>

                    <div className="tw-space-y-6">
                      <div className="tw-flex tw-items-center tw-gap-3 tw-mb-6">
                        <div className="tw-w-10 tw-h-10 tw-bg-primary tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-white tw-font-bold">
                          2
                        </div>
                        <h2 className="tw-text-xl tw-font-bold tw-text-gray-600">
                          Invite Colleagues
                        </h2>
                      </div>

                      <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
                        Help increase student practice and feedback by adding
                        colleagues.
                      </p>

                      <form.Field
                        name="invitedColleagues"
                        validators={{
                          onChange: ({ value }) =>
                            value.length === 0
                              ? "Please invite at least one colleague"
                              : undefined,
                        }}
                      >
                        {(field) => (
                          <Field>
                            <ColleagueInput
                              invitedColleagues={field.state.value}
                              onAdd={(email) =>
                                field.handleChange([
                                  ...field.state.value,
                                  email,
                                ])
                              }
                              onRemove={(email) =>
                                field.handleChange(
                                  field.state.value.filter((c) => c !== email),
                                )
                              }
                              onSearch={searchColleagues}
                              error={
                                field.state.meta.errors.length > 0
                                  ? field.state.meta.errors[0]
                                  : undefined
                              }
                              label="Add Team Members"
                              placeholder="Search by name or enter email..."
                            />
                          </Field>
                        )}
                      </form.Field>
                    </div>
                  </div>
                </div>

                <div className="tw-px-8 tw-py-6 tw-bg-gray-50 tw-border-t tw-border-gray-200">
                  <form.Subscribe
                    selector={(state) => [state.canSubmit, state.isSubmitting]}
                  >
                    {([canSubmit, isSubmitting]) => (
                      <Button
                        type="submit"
                        size="lg"
                        className="tw-w-full"
                        disabled={!canSubmit}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner className="tw-mr-2" />
                            Submitting...
                          </>
                        ) : (
                          "Submit Application"
                        )}
                      </Button>
                    )}
                  </form.Subscribe>
                  <p className="tw-text-center tw-text-xs tw-text-gray-500 tw-mt-3">
                    By submitting, you agree to our terms and conditions
                  </p>
                </div>
              </div>
            </form>

            <div className="tw-text-center tw-mt-8 tw-text-sm tw-text-gray-600">
              Questions? Email us at{" "}
              <span className="tw-font-semibold tw-text-white">
                pro@classkick.com
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
